<!--
  =====================================================================
  SECTION NEWS GRID - LIST VIEW OF LATEST NEWS
  =====================================================================
  
  Grid layout component displaying news items in a list format.
  
  FEATURES:
  - Responsive grid layout with date, title, description, link
  - Progressive loading with "load more" button
  - Automatic description extraction from first HTML section
  - HTML content stripping for plain text display
  - Formatted date output in Italian locale
  - Interactive hover effects
  - Click redirect to full article
  
  BEHAVIOR:
  - Shows 10 items by default, loads 10 more on demand
  - Extracts description from section content automatically
  - Handles missing/empty descriptions gracefully
  - Full-width responsive grid columns
  
  =====================================================================
-->

<template>
  <section class="news-list">
    
    <div 
      v-for="item in visibleNews" 
      :key="item.id" 
      class="news-item" 
      @click="handleNewsClick($event, item.id)"
    >
      <div class="news-date">{{ formatDate(item.date) }}</div>
      <h3 class="news-title">{{ item.title }}</h3>
      <p class="news-description">{{ getDescription(item) }}</p>
      <a 
        :href="getNewsLink(item.id)" 
        class="news-link" 
        @click.stop="handleNewsClick($event, item.id)"
      >
        Approfondisci
        <span class="arrow">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </a>
    </div>

    <div v-if="hasMoreNews" class="load-more-container">
      <button
        @click="loadMoreNews"
        class="load-more-button"
        :disabled="isLoading"
      >
        {{ isLoading ? 'Caricamento…' : 'Carica news precedenti' }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getNewsLinkById } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} content - Content object with items array
 */
const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE - PROGRESSIVE LOADING
// ───────────────────────────────────────────────────────────────────

const visibleCount = ref(10)
const isLoading = ref(false)
const newsPerLoad = 10

// ───────────────────────────────────────────────────────────────────
// NEWS DATA
// ───────────────────────────────────────────────────────────────────

const items = props.content?.items || []

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Returns visible news items based on current load count
 */
const visibleNews = computed(() => items.slice(0, visibleCount.value))

/**
 * Determines if more news items can be loaded
 */
const hasMoreNews = computed(() => visibleCount.value < items.length)

// ───────────────────────────────────────────────────────────────────
// EVENT HANDLERS - LOAD MORE
// ───────────────────────────────────────────────────────────────────

/**
 * Loads next batch of news items
 * Shows loading state during load
 */
const loadMoreNews = async () => {
  isLoading.value = true
  await new Promise(resolve => setTimeout(resolve, 300))
  const newCount = Math.min(visibleCount.value + newsPerLoad, items.length)
  visibleCount.value = newCount
  isLoading.value = false
}

// ───────────────────────────────────────────────────────────────────
// NAVIGATION & LINK UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Gets news article URL by ID
 * @param {String|Number} id - News item ID
 * @returns {String} News article URL
 */
function getNewsLink(id) {
  return getNewsLinkById(id)
}

/**
 * Handles news item click
 * Navigates to full article
 * @param {Event} event - Click event
 * @param {String|Number} id - News item ID
 */
function handleNewsClick(event, id) {
  event.preventDefault()
  const newsUrl = getNewsLink(id)
  window.location.href = newsUrl
}

// ───────────────────────────────────────────────────────────────────
// TEXT FORMATTING UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Formats date to Italian locale string
 * Example: "1 gennaio 2024"
 * @param {String|Date} d - Date to format
 * @returns {String} Formatted date string
 */
function formatDate(d) {
  return d
    ? new Date(d).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''
}

/**
 * Extracts description from news item
 * Gets first HTML-text section and strips HTML tags
 * Truncates to 120 characters
 * @param {Object} item - News item object
 * @returns {String} Plain text description
 */
function getDescription(item) {
  const firstSection = item.sections?.find(section => section.type === 'html-text')
  if (!firstSection?.content?.text) return ''
  const textOnly = firstSection.content.text.replace(/<[^>]*>/g, '').trim()
  return textOnly.length > 120 ? textOnly.substring(0, 120) + '...' : textOnly
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

.news-list {
  width: 100%;
  max-width: 100vw;
  padding: 0 $container-padding-x;
  overflow-x: hidden;
  box-sizing: border-box;
}

.news-item {
  display: grid;
  grid-template-columns: clamp(100px, 15vw, 180px) clamp(300px, 35vw, 400px) 1fr auto;
  grid-template-areas: "date title description link";
  align-items: center;
  gap: clamp(1rem, 3vw, 2rem);
  padding: clamp(1rem, 2.5vw, 1.5rem) 0;
  border-bottom: 1px solid $clr-border;
  transition: background-color 0.25s ease, transform 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-bg-light, 0.6);
      transform: translateX(4px);

      .news-title { color: $clr-tertiary; }
      .news-link { color: $clr-tertiary; transform: translateX(4px); }
    }
  }

  &:last-child { border-bottom: none; }

  .news-date {
    grid-area: date;
    font-size: $font-list-date;
    color: $clr-text-light;
    font-weight: 500;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .news-title {
    grid-area: title;
    font-size: $font-list-title;
    font-weight: 600;
    color: $text-color;
    line-height: 1.4;
    margin: 0;
    transition: color 0.25s ease;
    min-width: 0;
    overflow: hidden;
    @include line-clamp(2, 1.4, $overflow: false);
  }

  .news-description {
    grid-area: description;
    font-size: $font-list-meta;
    color: $clr-text-light;
    line-height: 1.4;
    margin: 0;
    text-align: left;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .news-link {
    grid-area: link;
    font-size: $font-list-meta;
    color: $clr-brand-dark;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: color 0.25s ease, transform 0.25s ease;
    justify-self: end;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.5rem);
  }
}

.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.2em;
  height: 1.2em;
  color: currentColor;
  transition: transform 0.3s ease;
  transform: rotate(-90deg);
  svg { width: 100%; height: 100%; }
}

@media (hover: hover) {
  .news-link:hover .arrow { transform: rotate(90deg); }
}

.load-more-container {
  width: 100%;
  padding: 0 $container-padding-x;
  margin-top: clamp(1.5rem, 3vw, 2.5rem);
  display: flex;
  justify-content: flex-start;
  box-sizing: border-box;
}

.load-more-button {
  background: none;
  border: none;
  color: $clr-brand-dark;
  font-size: $font-list-meta;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  padding: $space-0-75 $space-1-5;
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      color: $clr-tertiary;
      background-color: rgba($clr-tertiary, 0.1);
      transform: translateX(4px);
    }
  }

  &:disabled {
    color: $clr-text-light;
    cursor: not-allowed;
    transform: none;
  }

  &:focus {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
  }
}

@media (max-width: 1300px) {
  .news-list { padding: 0 $container-padding-x-mob; }
  .load-more-container { padding: 0 $container-padding-x-mob; }

  .news-item {
    grid-template-columns: clamp(90px, 12vw, 120px) clamp(280px, 32vw, 350px) 1fr auto;
    gap: clamp(0.8rem, 2.5vw, 1.8rem);
    padding: clamp(0.9rem, 2.2vw, 1.3rem) 0;

    .news-date { font-size: $font-list-date-lg; }
    .news-title { font-size: $font-list-title-lg; }
    .news-description,
    .news-link { font-size: $font-list-meta-lg; }
  }

  .load-more-button { font-size: $font-list-meta-lg; }
}

@media (max-width: 1024px) {
  .news-list { padding: 0 $container-padding-x-mob; }

  .news-item {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      "date title link"
      "description description description";
    gap: $space-0-75;
    padding: $space-1 0;

    .news-description { margin-top: $space-0-5; grid-column: 1 / -1; }
    .news-date { min-width: clamp(80px, 15vw, 100px); }
  }
}

@media (max-width: 768px) {
  .news-list { padding: 0 $container-padding-x-mob; }

  .news-item {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "date link"
      "title title"
      "description description";
    gap: $space-0-5;
    padding: $space-0-75 0;

    .news-title,
    .news-description { margin-top: $space-0-25; grid-column: 1 / -1; }
    .news-date { font-size: $font-list-date; min-width: auto; }
    .news-title { font-size: $font-list-title; }
    .news-description,
    .news-link { font-size: $font-list-meta; }
  }

  .load-more-button {
    font-size: $font-list-meta;
    padding: $space-0-5 $space-1;
  }
}

@media (max-width: 640px) {
  .news-item {
    grid-template-columns: 1fr;
    grid-template-areas:
      "date"
      "title"
      "description"
      "link";
    gap: $space-0-5;
    padding: $space-0-75 0;

    .news-link { justify-self: start; margin-top: $space-0-25; }
  }
}

@media (max-width: 480px) {
  .news-list { padding: 0 $container-padding-x-mob; }
  .load-more-container { padding: 0 $container-padding-x-mob; margin-top: $space-1; }

  .news-item {
    padding: $space-0-5 0;
    gap: $space-0-25;

    .news-date { font-size: $font-list-date; }
    .news-title {
      font-size: $font-list-title;
      line-height: 1.2;
      @include line-clamp-lines(3, 1.2);
    }
    .news-description { font-size: $font-list-meta; line-height: 1.3; }
    .news-link { font-size: $font-list-meta; margin-top: $space-0-25; }
  }

  .load-more-button { font-size: $font-list-meta; padding: $space-0-5 $space-0-75; }
}

@media (max-width: 360px) {
  .news-list { padding: 0 $space-0-25; }
  .load-more-container { padding: 0 $space-0-25; }

  .news-item {
    padding: $space-0-5 0;
    gap: $space-0-25;

    .news-date { font-size: $font-list-date; }
    .news-title { font-size: $font-list-title; line-height: 1.1; }
    .news-description { font-size: $font-list-meta; line-height: 1.2; }
    .news-link { font-size: $font-list-meta; }
  }

  .load-more-button { font-size: $font-list-meta; }
}
</style>